import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import {
  classifyWorkJourneyExpectedMemberIssue,
  type WorkJourneyExpectedMemberIssue,
} from '@src/util/sentry/workJourneyExpectedMemberError';

import {
  buildAssignedMembersIssueDescription,
  collectAssignedMemberIssueUsers,
  type MemberLike,
} from './assignedMembersIssueNotify';

export type WorkJourneyExpectedMemberIssueNotifyOptions = {
  members?: MemberLike[];
  matchUrl?: string;
};

const MESSAGE_ID_BY_KIND: Record<WorkJourneyExpectedMemberIssue['kind'], string> = {
  'missing-address': 'workJourney.members.missingAddress',
  'missing-jurisdiction-id': 'workJourney.members.missingJurisdictionId',
  'members-pending': 'workJourney.members.pendingOnboarding',
  'members-inactive': 'workJourney.members.inactive',
};

/**
 * Toast body for every expected work-journey member eligibility error.
 * Dispatches into `assignedMembersIssueNotify`.
 */
export const buildWorkJourneyExpectedMemberIssueDescription = (
  error: unknown,
  intl: IntlShape,
  options: WorkJourneyExpectedMemberIssueNotifyOptions = {},
): ReactNode | null => {
  const issue = classifyWorkJourneyExpectedMemberIssue(error);
  if (!issue) {
    return null;
  }

  const users = collectAssignedMemberIssueUsers(issue, options.members);
  const messageValues =
    issue.kind === 'missing-jurisdiction-id'
      ? {
          document:
            issue.documentKind ||
            intl.formatMessage({
              id: 'workJourney.members.missingJurisdictionId.documentFallback',
            }),
        }
      : undefined;

  return buildAssignedMembersIssueDescription(
    intl,
    MESSAGE_ID_BY_KIND[issue.kind],
    users,
    {
      ...options,
      ...(messageValues ? { messageValues } : {}),
    },
  );
};
